import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Snackbar, Fade, Button, Stack, LinearProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemIcon, useMediaQuery
} from '@mui/material';
import {
  Refresh, PieChart, CheckCircle, PendingActions, PlayCircle,
  NavigateBefore, NavigateNext, TrendingUp, Receipt, AttachMoney, Engineering
} from '@mui/icons-material';
import { getAllMonitoring, getPresentase } from '../API/bantuan';

const statusColor = (s) => {
  if (s === 'Selesai') return 'success';
  if (s === 'Sedang Berlangsung' || s === 'Dalam Proses') return 'warning';
  return 'info';
};

const PresentaseBantuan = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const isMobile = useMediaQuery('(max-width:800px)');
  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllMonitoring({ page, limit });
      setRows(res.data.data || []);
      setTotalItems(res.data.totalItems ?? res.data.pagination?.totalItems ?? 0);
      setTotalPages(res.data.totalPages ?? res.data.pagination?.totalPages ?? 1);
    } catch (err) {
      console.error(err);
      notify('Gagal mengambil data presentase', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(), 400);
    return () => clearTimeout(t);
  }, [fetchList]);

  const openDetail = async (bantuanId) => {
    setDetailOpen(true); setDetailLoading(true); setDetail(null);
    try {
      const res = await getPresentase(bantuanId);
      setDetail(res.data);
    } catch (err) {
      notify('Gagal memuat detail presentase', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const avgProgress = rows.length > 0
    ? Math.round(rows.reduce((a, r) => a + (Number(r.Presentase?.presentase ?? r.bantuan?.Presentase?.presentase ?? 0)), 0) / rows.length)
    : 0;
const contentStyle = isMobile 
? {} // No margins on mobile
: { marginLeft: "250px", marginRight: "20px" };

  return (
     <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
    <Box sx={{ mt: 1, mb: 2, width: "100%", maxWidth: "100%" }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PieChart color="primary" />
          <Typography variant="h5" fontWeight={700}>Presentase Proses Bantuan</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchList}>Muat Ulang</Button>
      </Box>

      {/* Rata-rata progress */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TrendingUp color="primary" fontSize="large" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Rata-rata Progress Semua Bantuan</Typography>
              <Typography variant="h4" fontWeight={700} color="primary">{avgProgress}%</Typography>
              <LinearProgress variant="determinate" value={avgProgress} sx={{ height: 10, borderRadius: 5, mt: 0.5 }} />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Daftar Presentase per Bantuan</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead style={{ backgroundColor: '#f5f7fa' }}>
                <tr>
                  {['No.', 'Nama Pemohon', 'Jenis Bantuan', 'Langkah', 'Progress', 'Aksi'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Tidak ada data.</td></tr>
                ) : rows.map((row, i) => {
                  const pres = row.Presentase?.presentase ?? row.bantuan?.Presentase?.presentase ?? 0;
                  const selesai = row.Presentase?.langkahSelesai ?? row.bantuan?.Presentase?.langkahSelesai ?? 0;
                  const total = row.Presentase?.totalLangkah ?? row.bantuan?.Presentase?.totalLangkah ?? 0;
                  return (
                    <tr key={row.id || row.bantuan?.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px' }}>{(page - 1) * limit + i + 1}</td>
                      <td style={{ padding: '10px', fontWeight: 600 }}>
                        {row.pemohon?.nama || row.bantuan?.pemohon?.nama || '-'}
                      </td>
                      <td style={{ padding: '10px' }}>{row.JenisBantuan || row.bantuan?.JenisBantuan || '-'}</td>
                      <td style={{ padding: '10px' }}>{selesai}/{total}</td>
                      <td style={{ padding: '10px', minWidth: 160 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" fontWeight={700} color="primary">{pres}%</Typography>
                          <LinearProgress variant="determinate" value={Number(pres)} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        </Stack>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <Button size="small" variant="contained" onClick={() => openDetail(row.id || row.bantuan?.id)}>Detail</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box component="select" value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                sx={{ px: 1, py: 0.8, border: '1px solid #ccc', borderRadius: 1, fontSize: 14 }}>
                {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </Box>
              <Typography variant="body2" color="text.secondary">Hal. {page} / {totalPages}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<NavigateBefore />} disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>{!isMobile && 'Prev'}</Button>
              <Button variant="outlined" size="small" endIcon={<NavigateNext />} disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{!isMobile && 'Next'}</Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog detail langkah */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PieChart color="primary" /> Detail Presentase
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : detail ? (
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{detail.presentase?.langkahSelesai}/{detail.presentase?.totalLangkah} langkah selesai</Typography>
                  <Typography fontWeight={700} color="primary">{detail.presentase?.presentase}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={Number(detail.presentase?.presentase) || 0}
                  sx={{ height: 10, borderRadius: 5, mt: 0.5 }} />
              </Box>

              <Typography variant="subtitle2" fontWeight={600}><Receipt fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />A. Progress Administrasi</Typography>
              <List dense>
                {(detail.langkah || []).map((l) => (
                  <ListItem key={l.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1, mb: 0.5 }}>
                    <ListItemIcon>
                      {l.status === 'Selesai' ? <CheckCircle color="success" /> :
                        l.status === 'Sedang Berlangsung' ? <PlayCircle color="warning" /> : <PendingActions color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${l.urutan}. ${l.tahap}`}
                      secondary={<><Chip size="small" color={statusColor(l.status)} label={l.status} sx={{ mr: 1 }} />{l.tanggalSelesai ? `Selesai: ${new Date(l.tanggalSelesai).toLocaleDateString('id-ID')}` : ''}</>}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="subtitle2" fontWeight={600}><AttachMoney fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />B. Progress Keuangan</Typography>
              <List dense>
                {(detail.progressKeuangan || []).map((l) => (
                  <ListItem key={l.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1, mb: 0.5 }}>
                    <ListItemIcon>
                      {l.status === 'Selesai' ? <CheckCircle color="success" /> :
                        l.status === 'Sedang Berlangsung' ? <PlayCircle color="warning" /> : <PendingActions color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${l.urutan}. ${l.tahap}`}
                      secondary={<Chip size="small" color={statusColor(l.status)} label={l.status} />}
                    />
                  </ListItem>
                ))}
                {(!detail.progressKeuangan || detail.progressKeuangan.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Belum ada data keuangan.</Typography>
                )}
              </List>

              <Typography variant="subtitle2" fontWeight={600}><Engineering fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />C. Progress Fisik</Typography>
              <List dense>
                {(detail.progressFisik || []).map((l) => (
                  <ListItem key={l.id} sx={{ border: '1px solid #f0f0f0', borderRadius: 1, mb: 0.5 }}>
                    <ListItemIcon>
                      {l.status === 'Selesai' ? <CheckCircle color="success" /> :
                        l.status === 'Sedang Berlangsung' ? <PlayCircle color="warning" /> : <PendingActions color="info" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${l.urutan}. ${l.tahap}`}
                      secondary={<Chip size="small" color={statusColor(l.status)} label={l.status} />}
                    />
                  </ListItem>
                ))}
                {(!detail.progressFisik || detail.progressFisik.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Belum ada data fisik.</Typography>
                )}
              </List>
            </Stack>
          ) : <Alert severity="warning">Data tidak ditemukan.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        TransitionComponent={Fade} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </div>
    </div>
  );
};

export default PresentaseBantuan;
